import { supabase, supabaseAdmin } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import type { AccountDeletionResult, DeletionResult } from '../models/types';

/**
 * Service for user management operations
 *
 * Note: This service provides comprehensive user management functionality,
 * including account deletion with proper cleanup of all associated data.
 */
export class UserService {
  /**
   * Delete a user account and all associated data
   * @param userId - The ID of the user to delete
   * @returns A promise that resolves to the result of the deletion operation
   */
  async deleteUserAccount(userId: string): Promise<AccountDeletionResult> {
    logger.info('Initiating account deletion process', LogCategory.AUTH, { userId });
    
    // Initialize the result object
    const deletionResult: AccountDeletionResult = {
      userId,
      success: false,
      results: [],
      timestamp: new Date().toISOString()
    };
    
    try {
      // Check if the admin client is available
      if (!supabaseAdmin) {
        throw new Error('Admin client not available. Make sure VUE_APP_SUPABASE_SERVICE_KEY is set in .env');
      }
      
      // 1. Delete user profile
      const profileResult = await this.deleteUserProfile(userId);
      deletionResult.results.push(profileResult);
      
      // 2. Delete user templates
      const templatesResult = await this.deleteUserTemplates(userId);
      deletionResult.results.push(templatesResult);
      
      // 3. Delete user documents
      const documentsResult = await this.deleteUserDocuments(userId);
      deletionResult.results.push(documentsResult);
      
      // 4. Delete user files
      const filesResult = await this.deleteUserFiles(userId);
      deletionResult.results.push(filesResult);
      
      // 5. Delete user forecasting processes
      const forecastingResult = await this.deleteUserForecastingProcesses(userId);
      deletionResult.results.push(forecastingResult);
      
      // 6. Delete user email templates
      const emailTemplatesResult = await this.deleteUserEmailTemplates(userId);
      deletionResult.results.push(emailTemplatesResult);
      
      // 7. Delete the user from Supabase Auth
      const authResult = await this.deleteUserAuth(userId);
      deletionResult.results.push(authResult);
      
      // Set overall success based on all operations
      deletionResult.success = deletionResult.results.every(result => result.success);
      
      // Log the final result
      if (deletionResult.success) {
        logger.info('User account deletion completed successfully', LogCategory.AUTH, { userId });
      } else {
        logger.error('User account deletion completed with errors', LogCategory.AUTH, { 
          userId, 
          results: deletionResult.results.filter(r => !r.success) 
        });
      }
      
      return deletionResult;
    } catch (error) {
      // Log the error
      logger.error('Error during account deletion process', LogCategory.AUTH, { userId, error });
      
      // Add the error to the result
      deletionResult.results.push({
        dataType: 'overall',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      });
      
      return deletionResult;
    }
  }
  
  /**
   * Delete a user's profile
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserProfile(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user profile', LogCategory.AUTH, { userId });
      
      const { error, count } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);
      
      if (error) throw error;
      
      logger.info('User profile deleted successfully', LogCategory.AUTH, { userId, count });
      
      return {
        dataType: 'profiles',
        success: true,
        count: count || 0
      };
    } catch (error) {
      logger.error('Error deleting user profile', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'profiles',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Delete a user's templates
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserTemplates(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user templates', LogCategory.AUTH, { userId });
      
      // First, get the count of templates to be deleted
      const { count: templateCount, error: countError } = await supabase
        .from('templates')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      if (countError) throw countError;
      
      // Then delete the templates
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('user_id', userId);
      
      if (error) throw error;
      
      logger.info('User templates deleted successfully', LogCategory.AUTH, { userId, count: templateCount });
      
      return {
        dataType: 'templates',
        success: true,
        count: templateCount || 0
      };
    } catch (error) {
      logger.error('Error deleting user templates', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'templates',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Delete a user's documents
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserDocuments(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user documents', LogCategory.AUTH, { userId });
      
      // First, get the count of documents to be deleted
      const { count: documentCount, error: countError } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId);
      
      if (countError) throw countError;
      
      // Then delete the documents
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('created_by', userId);
      
      if (error) throw error;
      
      // Also check for legacy documents
      const { count: legacyCount, error: legacyCountError } = await supabase
        .from('documents_legacy')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId);
      
      if (legacyCountError) throw legacyCountError;
      
      // Delete legacy documents
      const { error: legacyError } = await supabase
        .from('documents_legacy')
        .delete()
        .eq('created_by', userId);
      
      if (legacyError) throw legacyError;
      
      const totalCount = (documentCount || 0) + (legacyCount || 0);
      
      logger.info('User documents deleted successfully', LogCategory.AUTH, { 
        userId, 
        count: totalCount,
        documentCount,
        legacyCount
      });
      
      return {
        dataType: 'documents',
        success: true,
        count: totalCount
      };
    } catch (error) {
      logger.error('Error deleting user documents', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'documents',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Delete a user's files
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserFiles(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user files', LogCategory.AUTH, { userId });
      
      // For files, we might need to first get the files associated with the user
      // This depends on how files are associated with users in your system
      // For this example, we'll assume there's a user_id field in the files table
      
      // First, get the count of files to be deleted
      const { count: fileCount, error: countError } = await supabase
        .from('files')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      if (countError) throw countError;
      
      // Then delete the files
      const { error } = await supabase
        .from('files')
        .delete()
        .eq('user_id', userId);
      
      if (error) throw error;
      
      // Also delete any files in storage
      // This would require additional implementation specific to your storage setup
      // For example, you might have a storage bucket for each user
      
      logger.info('User files deleted successfully', LogCategory.AUTH, { userId, count: fileCount });
      
      return {
        dataType: 'files',
        success: true,
        count: fileCount || 0
      };
    } catch (error) {
      logger.error('Error deleting user files', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'files',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Delete a user's forecasting processes
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserForecastingProcesses(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user forecasting processes', LogCategory.AUTH, { userId });
      
      // For forecasting processes, we need to determine how they're associated with users
      // For this example, we'll assume there's a created_by field
      
      // First, get the count of processes to be deleted
      const { count: processCount, error: countError } = await supabase
        .from('forecasting_process')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId);
      
      if (countError) throw countError;
      
      // Then delete the processes
      const { error } = await supabase
        .from('forecasting_process')
        .delete()
        .eq('created_by', userId);
      
      if (error) throw error;
      
      logger.info('User forecasting processes deleted successfully', LogCategory.AUTH, { userId, count: processCount });
      
      return {
        dataType: 'forecasting_process',
        success: true,
        count: processCount || 0
      };
    } catch (error) {
      logger.error('Error deleting user forecasting processes', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'forecasting_process',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Delete a user's email templates
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserEmailTemplates(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user email templates', LogCategory.AUTH, { userId });
      
      // For email templates, we'll assume there's a created_by field
      
      // First, get the count of templates to be deleted
      const { count: templateCount, error: countError } = await supabase
        .from('email_templates')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId)
        .eq('is_system', false); // Don't delete system templates
      
      if (countError) throw countError;
      
      // Then delete the templates
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('created_by', userId)
        .eq('is_system', false); // Safety check to not delete system templates
      
      if (error) throw error;
      
      logger.info('User email templates deleted successfully', LogCategory.AUTH, { userId, count: templateCount });
      
      return {
        dataType: 'email_templates',
        success: true,
        count: templateCount || 0
      };
    } catch (error) {
      logger.error('Error deleting user email templates', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'email_templates',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Delete a user from Supabase Auth
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  /**
   * Delete a user from Supabase Auth (public method for direct use)
   * @param userIdentifier - The ID or email of the user
   * @returns A promise that resolves to true if successful
   */
  async deleteUserAuthOnly(userIdentifier: string): Promise<boolean> {
    try {
      logger.info('Deleting user from Supabase Auth (direct method)', LogCategory.AUTH, { userIdentifier });
      
      // This requires admin privileges, so we use the supabaseAdmin client
      if (!supabaseAdmin) {
        throw new Error('Admin client not available. Make sure VUE_APP_SUPABASE_SERVICE_KEY is set in .env');
      }
      
      // Check if the identifier is an email
      const isEmail = userIdentifier.includes('@');
      
      let userId = userIdentifier;
      
      // If it's an email, we need to find the user ID first
      if (isEmail) {
        logger.info('Email provided as identifier, looking up user ID', LogCategory.AUTH, { email: userIdentifier });
        
        // Get the user ID from the profiles table
        const { data, error: lookupError } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', userIdentifier)
          .single();
        
        if (lookupError || !data) {
          logger.error('Error looking up user ID from email', LogCategory.AUTH, {
            email: userIdentifier,
            error: lookupError
          });
          throw new Error(`Could not find user with email ${userIdentifier}`);
        }
        
        userId = data.id;
        logger.info('Found user ID for email', LogCategory.AUTH, { email: userIdentifier, userId });
      }
      
      // Now delete the user with the ID
      const { error } = await supabaseAdmin.auth.admin.deleteUser(userId);
      
      if (error) throw error;
      
      logger.info('User deleted from Supabase Auth successfully (direct method)', LogCategory.AUTH, { userId });
      
      return true;
    } catch (error) {
      logger.error('Error deleting user from Supabase Auth (direct method)', LogCategory.AUTH, { userIdentifier, error });
      throw error;
    }
  }

  /**
   * Delete a user from Supabase Auth (internal method)
   * @param userId - The ID of the user
   * @returns A promise that resolves to the result of the deletion operation
   */
  private async deleteUserAuth(userId: string): Promise<DeletionResult> {
    try {
      logger.info('Deleting user from Supabase Auth', LogCategory.AUTH, { userId });
      
      // This requires admin privileges, so we use the supabaseAdmin client
      if (!supabaseAdmin) {
        throw new Error('Admin client not available. Make sure VUE_APP_SUPABASE_SERVICE_KEY is set in .env');
      }
      
      const { error } = await supabaseAdmin.auth.admin.deleteUser(userId);
      
      if (error) throw error;
      
      logger.info('User deleted from Supabase Auth successfully', LogCategory.AUTH, { userId });
      
      return {
        dataType: 'auth',
        success: true,
        count: 1
      };
    } catch (error) {
      logger.error('Error deleting user from Supabase Auth', LogCategory.AUTH, { userId, error });
      
      return {
        dataType: 'auth',
        success: false,
        count: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
}